import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { finalize, tap } from 'rxjs';
import { AuthResponse } from '../models/auth.model';
import { AuthService } from '../services/auth.service';

// WHY: Route mapping for access levels - absolute paths to child routes
const routeMap: Record<NonNullable<AuthResponse['access_level']>, string> = {
  'full': 'details',
  'polterabend': 'polter'
} as const;

/**
 * WHY: Centralized login page following Angular 20 signal-based patterns
 * SOLID: Single responsibility - handles only login flow and password visibility
 */
@Component({
  selector: 'app-login',
  templateUrl: './login.html',
  imports: [CommonModule, FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LoginPage {
  private readonly router = inject(Router);
  private readonly auth = inject(AuthService);

  protected readonly accessword = signal('');
  protected readonly loading = signal(false);
  protected readonly error = signal<string | null>(null);
  protected readonly showPassword = signal(false);

  protected readonly canLogin = computed(() =>
    this.accessword().length > 0 && !this.loading()
  );

  protected onLogin(): void {
    if (!this.canLogin()) return;

    console.log('Starting login with password:', this.accessword().substring(0, 3) + '***');

    this.auth.login(this.accessword())
      .pipe(
        tap(() => {
          this.error.set(null);
          this.loading.set(true);
        }),
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        next: (response) => {
          if (!response || !(response.valid && response.access_level)) {
            const errorMessage = response?.message ?? 'Ungültiges Zugangswort. Bitte versuche es erneut.';
            this.error.set(errorMessage);
          } else {
            if (response.access_level)
              this.navigateToDestination(response.access_level);
          }

        },
        error: (error) => {
          const errorMessage = this.getErrorMessage(error);
          this.error.set(errorMessage);
        }
      });
  }

  protected togglePasswordVisibility(): void {
    this.showPassword.update(current => !current);
  }


  private getErrorMessage(error: any): string {
    // WHY: Centralized error handling with specific backend unavailable detection
    const isBackendUnavailable = error.status === 0 ||
      (error.name === 'HttpErrorResponse' && error.error instanceof ProgressEvent);

    return isBackendUnavailable
      ? 'Das System ist aktuell nicht erreichbar. Bitte versuche es später erneut.'
      : 'Fehler beim Prüfen des Zugangsworts.';
  }

  private navigateToDestination(accessLevel: NonNullable<AuthResponse['access_level']>): void {
    const destination = routeMap[accessLevel];
    if (!destination) {
      this.error.set('Unbekannter Fehler. Bitte versuche es später erneut.');
      return;
    }
    this.router.navigate(['/' + destination]).then(success => {
      if (success) {
        console.log('Navigation successful:', '/' + destination);
      } else {
        console.error('Navigation error:', '/' + destination);
        this.error.set('Navigation fehlgeschlagen.');
      }
    }).catch(err => {
      console.error('Navigation error:', err);
      this.error.set('Navigation fehlgeschlagen.');
    });
  }
}
